import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

// Auth now lives in an httpOnly cookie set by POST /auth/login — nothing is
// persisted in localStorage. One-time migration note: users logged in before
// the cookie switch had their JWT in localStorage only; they get logged out
// once after deploy and must sign in again (acceptable).
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // In-memory copy of the access token from the login response. NOT persisted.
  // Only used as a query-param fallback for WebSocket handshakes in
  // cross-origin dev setups where the cookie isn't sent; lost on refresh,
  // at which point same-origin cookie auth takes over.
  const [wsToken, setWsToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      // The httpOnly cookie rides along automatically; 401 means unauthenticated.
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch {
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setWsToken(data.access_token);
    setUser(data.user);
    return data;
  };

  const logout = () => {
    api.post('/auth/logout').catch(() => {}); // clears the httpOnly cookie
    setWsToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, wsToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
